import { useRef } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, MapPin, ShieldCheck, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const headline = ['Land with', 'conviction.', 'Wealth with', 'momentum.'];

const Hero = () => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [7, -7]), { stiffness: 130, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 130, damping: 20 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);
  const haloY = useTransform(scrollYProgress, [0, 1], ['0%', '-22%']);

  const goContact = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section ref={sectionRef} id="hero" className="blueprint-grid relative min-h-screen overflow-hidden bg-[#F5F5F5] pt-28" data-testid="hero-section">
      <motion.div style={{ y: haloY }} className="absolute left-[8%] top-24 h-72 w-72 rounded-full bg-[#C5A880]/20 blur-3xl" />
      <div className="absolute bottom-16 left-0 hidden font-tech text-[10px] uppercase tracking-[0.3em] text-[#0B1F3B]/35 lg:block" style={{ writingMode: 'vertical-rl' }}>
        19.0760° N / 72.8777° E — Mumbai mandate corridor
      </div>

      <div className="mx-auto grid min-h-[calc(100vh-7rem)] max-w-7xl items-center gap-12 px-4 pb-20 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }} className="mb-7 inline-flex items-center gap-3 rounded-full border border-[#0B1F3B]/10 bg-white/70 px-4 py-2 shadow-sm" data-testid="hero-mandate-badge">
            <span className="h-2 w-2 rounded-full bg-[#1E7B54]" />
            <span className="font-tech text-[11px] uppercase tracking-[0.24em] text-[#0B1F3B]/70">50+ units absorbed · under 8 months</span>
          </motion.div>

          <h1 className="max-w-4xl font-display text-4xl font-semibold leading-[1.04] tracking-[-0.035em] text-[#0B1F3B] sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            {headline.map((line, index) => (
              <span key={line} className="hero-mask-line pb-1">
                <motion.span initial={{ y: '112%' }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.16 + index * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                  {line === 'momentum.' ? <em className="text-[#C5A880]">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72, duration: 0.7 }} className="mt-7 max-w-xl text-base leading-relaxed text-[#0B1F3B]/70 sm:text-lg">
            Next Leap Inc is a mandate-first real estate practice for plotted developments and high-conviction residential assets across India — led personally by Vikrant Pawar and Bhagwandas Kewat.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.86, duration: 0.7 }} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button onClick={() => navigate('/projects')} data-testid="hero-explore-mandates-btn" className="group h-[52px] rounded-full bg-[#0B1F3B] px-7 text-sm font-semibold text-white shadow-[0_18px_50px_rgba(11,31,59,0.22)] transition-all hover:-translate-y-1 hover:bg-[#06101E]">
              Explore 11 live mandates
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button onClick={goContact} data-testid="hero-inquire-cta-btn" variant="outline" className="h-[52px] rounded-full border-[#0B1F3B]/20 bg-white/60 px-7 text-sm font-semibold text-[#0B1F3B] transition-all hover:-translate-y-1 hover:bg-white">
              Request private consultation
            </Button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.02, duration: 0.8 }} className="mt-12 grid max-w-2xl grid-cols-3 divide-x divide-[#0B1F3B]/10 border-y border-[#0B1F3B]/10 py-5" data-testid="hero-metrics">
            {[
              ['50+', 'Inventories'],
              ['<8', 'Months'],
              ['12+', 'Developers'],
            ].map(([value, label]) => (
              <div key={label} className="px-4 first:pl-0">
                <div className="font-display text-3xl font-semibold text-[#0B1F3B]">{value}</div>
                <div className="mt-1 font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B]/50">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 28 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.42, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
            mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
          }}
          onMouseLeave={() => { mouseX.set(0); mouseY.set(0); }}
          style={{ rotateX, rotateY, transformPerspective: 1100 }}
          className="relative mx-auto w-full max-w-xl"
          data-testid="hero-estate-card"
        >
          <div className="absolute -left-6 top-10 h-full w-full rounded-[2rem] border border-[#C5A880]/45" />
          <div className="relative overflow-hidden rounded-[2rem] bg-[#0B1F3B] shadow-[0_40px_100px_rgba(6,16,30,0.34)]">
            <motion.img style={{ y: imageY }} src="https://customer-assets.emergentagent.com/job_plotted-homes-india/artifacts/68h9440a_beautiful-landscape-contained-photo-frame.jpg.jpeg" alt="Curated Indian estate landscape" className="h-[560px] w-full scale-110 object-cover opacity-95" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06101E]/80 via-transparent to-[#F5F5F5]/10" />
            <div className="absolute left-5 top-5 rounded-full bg-[#F5F5F5]/90 px-4 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B] backdrop-blur-md">
              <MapPin className="mr-1 inline h-3.5 w-3.5" /> India portfolio
            </div>
            <div className="absolute bottom-5 left-5 right-5 rounded-3xl border border-white/10 bg-[#06101E]/60 p-5 text-white backdrop-blur-xl">
              <div className="mb-3 flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.22em] text-[#C5A880]"><ShieldCheck className="h-4 w-4" /> Mandate verified</div>
              <div className="font-display text-2xl">From raw acreage to precise absorption.</div>
            </div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} className="absolute right-5 top-24 rounded-2xl border border-white/20 bg-white/10 p-4 text-white backdrop-blur-xl">
              <Sparkles className="mb-2 h-5 w-5 text-[#C5A880]" />
              <div className="font-tech text-[10px] uppercase tracking-[0.18em]">Live mandate desk</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
